import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, AlertTriangle, Clock, Calculator } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * MarginAuthorizationPublic — public route ``/margin-authorization/:token``.
 *
 * Admins receive an email with this link when a gestor submits a request
 * for a below-threshold margin. The page:
 *   1. Loads the request by token (no auth required — token is the key).
 *   2. Shows the snapshot: category, revenue, AF cost, margin vs threshold,
 *      reason from the gestor.
 *   3. Requires the admin to type their email + optional decision reason
 *      before hitting approve/reject.
 */

const EUR = (v) => new Intl.NumberFormat("pt-PT", {
  style: "currency", currency: "EUR",
}).format(Number(v || 0));

const CATEGORY_LABEL = {
  continuidade_full: "Continuidade ≥ 12h/dia",
  continuidade_partial: "Continuidade < 12h/dia",
  pontual: "Serviço pontual",
};

const STATUS_META = {
  pending:   { label: "Pendente", icon: Clock, tone: "text-[hsl(var(--warning))] bg-[hsl(var(--warning-bg))] border-[hsl(var(--warning))]/25" },
  approved:  { label: "Aprovado", icon: CheckCircle2, tone: "text-[hsl(var(--success))] bg-[hsl(var(--success-bg))] border-[hsl(var(--success))]/25" },
  rejected:  { label: "Rejeitado", icon: XCircle, tone: "text-[hsl(var(--danger))] bg-[hsl(var(--danger-bg))] border-[hsl(var(--danger))]/25" },
  expired:   { label: "Expirado", icon: AlertTriangle, tone: "text-muted-foreground bg-muted/40 border-border" },
  cancelled: { label: "Cancelado", icon: XCircle, tone: "text-muted-foreground bg-muted/40 border-border" },
};

export default function MarginAuthorizationPublic() {
  const { token } = useParams();
  const [req, setReq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/margin-authorizations/${token}`)
      .then((r) => { if (!cancelled) setReq(r.data); })
      .catch((e) => { if (!cancelled) setErr(e?.response?.data?.detail || "Pedido não encontrado."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  const decide = async (decision) => {
    if (!email.trim()) {
      toast.error("Introduz o teu email para validar a identidade.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/margin-authorizations/${token}/decide`, {
        decision,
        approver_email: email.trim(),
        decision_reason: reason.trim() || null,
      });
      setReq(r.data);
      toast.success(`Pedido ${decision === "approve" ? "aprovado" : "rejeitado"}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao registar decisão.");
    } finally { setBusy(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-muted/40 flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-muted-foreground animate-spin" />
      </div>
    );
  }
  if (err || !req) {
    return (
      <div className="min-h-screen bg-muted/40 flex items-center justify-center p-6">
        <div className="max-w-md bg-card border border-border rounded-xl p-8 text-center">
          <AlertTriangle className="w-10 h-10 text-muted-foreground mx-auto mb-4" strokeWidth={1.4} />
          <h1 className="font-heading text-lg text-foreground">Pedido não encontrado</h1>
          <p className="text-[13px] text-muted-foreground mt-2">{err || "O link pode ter expirado."}</p>
        </div>
      </div>
    );
  }

  const meta = STATUS_META[req.status] || STATUS_META.pending;
  const StatusIcon = meta.icon;
  const isPending = req.status === "pending";

  return (
    <div className="min-h-screen bg-muted/40 p-6 lg:p-10" data-testid="margin-auth-root">
      <div className="max-w-2xl mx-auto space-y-6">

        {/* Header card */}
        <header className="bg-card border border-border rounded-xl p-6">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-lg bg-white/10 text-white flex items-center justify-center shrink-0">
              <Calculator className="w-5 h-5" strokeWidth={1.6} />
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-heading font-semibold">
                Caring · Autorização de margem
              </div>
              <h1 className="font-heading text-2xl tracking-tight text-foreground leading-tight">
                Pedido de autorização especial
              </h1>
              <div className="mt-2 text-[13px] text-muted-foreground">
                Cliente: <span className="text-foreground font-medium">{req.lead_name}</span> ·
                Pedido por <span className="text-foreground font-medium">{req.requested_by_name || "gestor"}</span>
              </div>
            </div>
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-medium ${meta.tone}`}
                  data-testid={`margin-auth-status-${req.status}`}>
              <StatusIcon className="w-3 h-3" /> {meta.label}
            </span>
          </div>
        </header>

        {/* Numbers */}
        <section className="bg-card border border-border rounded-xl p-6" data-testid="margin-auth-snapshot">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-heading font-semibold mb-4">
            Simulação submetida
          </div>
          <dl className="grid grid-cols-2 gap-6">
            <div>
              <dt className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Categoria</dt>
              <dd className="text-[15px] font-heading text-foreground mt-1">
                {CATEGORY_LABEL[req.service_category] || req.service_category}
              </dd>
            </div>
            <div>
              <dt className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Mínimo obrigatório</dt>
              <dd className="text-[15px] font-heading text-foreground tabular-nums mt-1">{req.threshold_pct}%</dd>
            </div>
            <div>
              <dt className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Receita mensal</dt>
              <dd className="text-[15px] font-heading text-foreground tabular-nums mt-1">{EUR(req.monthly_revenue)}</dd>
            </div>
            <div>
              <dt className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Custo AF</dt>
              <dd className="text-[15px] font-heading text-foreground tabular-nums mt-1">{EUR(req.monthly_af_cost)}</dd>
            </div>
            <div className="col-span-2 border-t border-border/60 pt-4">
              <dt className="text-[11px] uppercase tracking-wider text-[hsl(var(--danger))] font-semibold">Margem proposta</dt>
              <dd className="text-3xl font-heading font-bold text-[hsl(var(--danger))] tabular-nums mt-1">
                {req.margin_pct}%
                <span className="text-[13px] text-muted-foreground font-medium ml-2">
                  ({(req.monthly_revenue - req.monthly_af_cost).toFixed(2)}€ absoluto)
                </span>
              </dd>
            </div>
          </dl>
        </section>

        {/* Reason */}
        <section className="bg-card border border-border border-l-4 border-l-blue-500 rounded-xl p-6"
                 data-testid="margin-auth-reason">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-heading font-semibold mb-2">
            Motivo do gestor
          </div>
          <p className="text-[14px] text-foreground leading-relaxed whitespace-pre-wrap">
            {req.reason || <em className="text-muted-foreground">sem motivo indicado</em>}
          </p>
        </section>

        {/* Decision form */}
        {isPending ? (
          <section className="bg-card border border-border rounded-xl p-6" data-testid="margin-auth-decision">
            <h2 className="font-heading text-lg tracking-tight text-foreground mb-4">Decisão</h2>
            <div className="space-y-3">
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-1.5">
                  O teu email (só admins)
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@caring.pt"
                  data-testid="margin-auth-email"
                  className="w-full h-10 px-3 border border-border rounded-md text-[14px] bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-[#1D4ED8]/25 focus:border-primary"
                />
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-1.5">
                  Notas (opcional)
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                  placeholder="Contexto que queiras deixar no registo…"
                  data-testid="margin-auth-decision-reason"
                  className="w-full px-3 py-2 border border-border rounded-md text-[13px] resize-none bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-[#1D4ED8]/25 focus:border-primary"
                />
              </div>
              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => decide("reject")}
                  data-testid="margin-auth-reject"
                  className="h-10 px-5 rounded-md bg-card border border-[hsl(var(--danger))]/40 text-[hsl(var(--danger))] font-medium hover:bg-[hsl(var(--danger-bg))] disabled:opacity-60 flex items-center gap-2 font-heading"
                >
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <XCircle className="w-4 h-4" />}
                  Rejeitar
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => decide("approve")}
                  data-testid="margin-auth-approve"
                  className="h-10 px-5 rounded-md bg-emerald-600 text-white font-medium hover:bg-emerald-700 disabled:opacity-60 flex items-center gap-2 font-heading"
                >
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                  Aprovar
                </button>
              </div>
            </div>
          </section>
        ) : (
          <section className={`bg-card border rounded-xl p-6 ${meta.tone}`} data-testid="margin-auth-decided">
            <div className="text-[10px] uppercase tracking-[0.2em] font-heading font-semibold mb-2 opacity-70">
              Decisão registada
            </div>
            <p className="text-[13px]">
              {meta.label} por <b>{req.decided_by_name || req.decided_by_email}</b> em{" "}
              <span className="tabular-nums">{req.decided_at?.slice(0, 16).replace("T", " ")}</span>
            </p>
            {req.decision_reason && (
              <p className="text-[13px] mt-2 whitespace-pre-wrap opacity-90">{req.decision_reason}</p>
            )}
          </section>
        )}

        <footer className="text-center text-[11px] text-muted-foreground pt-4">
          Pedido criado em {req.requested_at?.slice(0, 16).replace("T", " ")} · expira em {req.expires_at?.slice(0, 16).replace("T", " ")}
        </footer>
      </div>
    </div>
  );
}
